/**
 * TopologyGraph - Interactive force-directed network topology visualization
 * 
 * Uses react-force-graph-2d for React-native force simulation.
 * Node types: edge, scatter, consensus, ops, bridge, cloudflare
 * Link types: tunnel, udp, webtransport, vpc
 */

import { useRef, useCallback, useState, useEffect } from 'react';
import ForceGraph2D, { ForceGraphMethods } from 'react-force-graph-2d';
import { TopologyNode, TopologySnapshot } from '../hooks/useHttpPolling';
import './TopologyGraph.css';

// Node type colors matching legacy console
const NODE_COLORS: Record<string, string> = {
  edge: '#0ea5e9',       // Sky blue
  scatter: '#8b5cf6',    // Purple
  consensus: '#f59e0b',  // Amber
  ops: '#22c55e',        // Green
  bridge: '#ef4444',     // Red
  cloudflare: '#f38020', // Orange
  client: '#6b7280',     // Gray
  unknown: '#94a3b8',    // Slate
};

// Status colors
const STATUS_COLORS: Record<string, string> = {
  healthy: '#22c55e',
  degraded: '#f59e0b',
  error: '#ef4444',
  offline: '#6b7280',
};

// Link type styles
const LINK_STYLES: Record<string, { color: string; width: number; dash?: number[] }> = {
  tunnel: { color: '#8b5cf6', width: 2 },
  udp: { color: '#0ea5e9', width: 1.5 },
  webtransport: { color: '#22c55e', width: 1.5, dash: [5, 5] },
  vpc: { color: '#f59e0b', width: 2, dash: [10, 5] },
};

// GraphNode includes x/y which are set by the force simulation
interface GraphNode {
  id: string;
  type: string;
  status: string;
  region: string;
  cloud: string;
  version: string | null;
  metrics?: TopologyNode['metrics'];
  // Properties set by force simulation
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  fx?: number;
  fy?: number;
}

// GraphLink - source/target become objects after simulation runs
interface GraphLink {
  id: string;
  type: string;
  status: string;
  source: string | GraphNode;
  target: string | GraphNode;
}

interface TopologyGraphProps {
  topology: TopologySnapshot;
  onNodeClick?: (node: TopologyNode) => void;
  onNodeHover?: (node: TopologyNode | null) => void;
  height?: number;
}

export default function TopologyGraph({ 
  topology, 
  onNodeClick,
  onNodeHover,
  height = 500 
}: TopologyGraphProps) {
  const graphRef = useRef<ForceGraphMethods<GraphNode, GraphLink>>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height });
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null);

  // Resize observer for container
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: height,
        });
      }
    });
    
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [height]);

  // Convert topology to graph data format
  const graphData = {
    nodes: topology.nodes.map((node) => ({
      id: node.id,
      type: node.type || 'unknown',
      status: node.status || 'offline',
      region: node.region || 'unknown',
      cloud: node.cloud || 'unknown',
      version: node.version,
      metrics: node.metrics,
    })),
    links: topology.links.map((link) => ({
      id: link.id,
      source: link.source,
      target: link.target,
      type: link.type || 'udp',
      status: link.status || 'active',
    })),
  };

  // Custom node rendering
  const nodeCanvasObject = useCallback((node: GraphNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const size = 8;
    const x = node.x || 0;
    const y = node.y || 0;
    const isHovered = hoveredNode?.id === node.id;
    
    // Node color based on type
    const nodeColor = NODE_COLORS[node.type] || NODE_COLORS.unknown;
    
    // Status indicator ring
    const statusColor = STATUS_COLORS[node.status] || STATUS_COLORS.offline;
    
    // Draw outer ring (status indicator)
    ctx.beginPath();
    ctx.arc(x, y, size + 2, 0, 2 * Math.PI);
    ctx.fillStyle = isHovered ? '#ffffff' : statusColor;
    ctx.fill();
    
    // Draw node
    ctx.beginPath();
    ctx.arc(x, y, size, 0, 2 * Math.PI);
    ctx.fillStyle = nodeColor;
    ctx.fill();
    
    // Draw label on hover or if zoomed in
    if (isHovered || globalScale > 1.5) {
      const label = `${node.type} (${node.region})`;
      const fontSize = isHovered ? 12 : 10;
      ctx.font = `${fontSize / globalScale}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(label, x, y + size + 4);
      
      // Version on hover
      if (isHovered && node.version) {
        ctx.font = `${9 / globalScale}px monospace`;
        ctx.fillStyle = '#94a3b8';
        ctx.fillText(`v${node.version}`, x, y + size + 18);
      }
    }
  }, [hoveredNode]);

  // Custom link rendering
  const linkCanvasObject = useCallback((link: GraphLink, ctx: CanvasRenderingContext2D) => {
    // After simulation, source/target become node objects
    const source = typeof link.source === 'object' ? link.source : null;
    const target = typeof link.target === 'object' ? link.target : null;
    
    if (!source || !target) return;
    if (source.x === undefined || source.y === undefined || target.x === undefined || target.y === undefined) return;
    
    const style = LINK_STYLES[link.type] || LINK_STYLES.udp;
    const isActive = link.status === 'active';
    
    ctx.beginPath();
    ctx.moveTo(source.x, source.y);
    ctx.lineTo(target.x, target.y);
    ctx.strokeStyle = isActive ? style.color : '#374151';
    ctx.lineWidth = style.width;
    
    if (style.dash) {
      ctx.setLineDash(style.dash);
    } else {
      ctx.setLineDash([]);
    }
    
    ctx.stroke();
    ctx.setLineDash([]);
  }, []);

  // Handle node click
  const handleNodeClick = useCallback((node: GraphNode) => {
    // Center view on node
    graphRef.current?.centerAt(node.x, node.y, 1000);
    graphRef.current?.zoom(2, 1000);
    
    // Find original node data
    const originalNode = topology.nodes.find(n => n.id === node.id);
    if (originalNode && onNodeClick) {
      onNodeClick(originalNode);
    }
  }, [topology.nodes, onNodeClick]);

  // Handle node hover
  const handleNodeHover = useCallback((node: GraphNode | null) => {
    setHoveredNode(node);
    if (onNodeHover) {
      const originalNode = node ? topology.nodes.find(n => n.id === node.id) || null : null;
      onNodeHover(originalNode);
    }
  }, [topology.nodes, onNodeHover]);

  // Reset view
  const handleReset = useCallback(() => {
    graphRef.current?.zoomToFit(400, 50);
  }, []);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    const currentZoom = graphRef.current?.zoom() || 1;
    graphRef.current?.zoom(currentZoom * 1.5, 300);
  }, []);

  const handleZoomOut = useCallback(() => {
    const currentZoom = graphRef.current?.zoom() || 1;
    graphRef.current?.zoom(currentZoom / 1.5, 300);
  }, []);

  return (
    <div className="topology-graph-container" ref={containerRef}>
      {/* Controls */}
      <div className="graph-controls">
        <button className="graph-control-btn" onClick={handleZoomIn} title="Zoom In">+</button>
        <button className="graph-control-btn" onClick={handleZoomOut} title="Zoom Out">−</button>
        <button className="graph-control-btn" onClick={handleReset} title="Reset View">⌂</button>
      </div>
      
      {/* Legend */}
      <div className="graph-legend">
        <div className="legend-section">
          <span className="legend-title">Nodes</span>
          {Object.entries(NODE_COLORS).filter(([k]) => k !== 'unknown').map(([type, color]) => (
            <div key={type} className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: color }} />
              <span className="legend-label">{type}</span>
            </div>
          ))}
        </div>
        <div className="legend-section">
          <span className="legend-title">Links</span>
          {Object.entries(LINK_STYLES).map(([type, style]) => (
            <div key={type} className="legend-item">
              <span 
                className="legend-line" 
                style={{ 
                  backgroundColor: style.color,
                  borderStyle: style.dash ? 'dashed' : 'solid'
                }} 
              />
              <span className="legend-label">{type}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Graph */}
      <ForceGraph2D
        ref={graphRef}
        graphData={graphData}
        width={dimensions.width}
        height={dimensions.height}
        nodeCanvasObject={nodeCanvasObject}
        nodeCanvasObjectMode={() => 'replace'}
        linkCanvasObject={linkCanvasObject}
        linkCanvasObjectMode={() => 'replace'}
        onNodeClick={handleNodeClick}
        onNodeHover={handleNodeHover}
        enableNodeDrag={true}
        enableZoomInteraction={true}
        cooldownTicks={100}
        onEngineStop={() => graphRef.current?.zoomToFit(400, 50)}
        backgroundColor="#0f172a"
      />
      
      {/* Hover tooltip */}
      {hoveredNode && (
        <div className="node-tooltip">
          <div className="tooltip-header">
            <span className="tooltip-type" style={{ color: NODE_COLORS[hoveredNode.type] }}>
              {hoveredNode.type}
            </span>
            <span className={`tooltip-status status-${hoveredNode.status}`}>
              {hoveredNode.status}
            </span>
          </div>
          <div className="tooltip-id">{hoveredNode.id.slice(0, 24)}...</div>
          <div className="tooltip-info">
            <span>{hoveredNode.cloud}</span>
            <span>{hoveredNode.region}</span>
          </div>
          {hoveredNode.metrics && (
            <div className="tooltip-metrics">
              <span>CPU: {hoveredNode.metrics.cpuPercent?.toFixed(1)}%</span>
              <span>Mem: {hoveredNode.metrics.memoryPercent?.toFixed(1)}%</span>
              <span>Conn: {hoveredNode.metrics.connections}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
